import React, { useState, useEffect, useMemo } from "react";
import { ref, onValue } from "../lib/firebase-database";
import { auth, db } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import { useNavigate, Link } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import { Loader2, Phone, Mail, Search, Plus } from "lucide-react";

export default function UsersPage() {
  const [owners, setOwners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }

    setIsLoading(true);
    setError(null);
    const villasRef = ref(db, "villas");
    onValue(
      villasRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const ownersMap = new Map();
          Object.entries(data).forEach(([villaId, villaData]) => {
            const { owner, ownerEmail, whatsappNumber } = villaData;
            if (owner && owner.name) {
              if (!ownersMap.has(ownerEmail)) {
                ownersMap.set(ownerEmail, {
                  name: owner.name,
                  email: ownerEmail,
                  photoURL: owner.image,
                  whatsappNumber,
                  ownedVillas: [{ id: villaId, name: villaData.name }],
                });
              } else {
                ownersMap
                  .get(ownerEmail)
                  .ownedVillas.push({ id: villaId, name: villaData.name });
              }
            }
          });
          setOwners(Array.from(ownersMap.values()));
        } else {
          setOwners([]);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error("Error fetching owners:", err);
        setError(`Failed to load owners: ${err.message}`);
        setIsLoading(false);
      }
    );
  }, [user, isAdmin, navigate]);

  const handleLogout = () => {
    auth.signOut().then(() => navigate("/login"));
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return owners;
    return owners.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        o.email?.toLowerCase().includes(q) ||
        o.whatsappNumber?.toLowerCase?.().includes(q)
    );
  }, [owners, searchQuery]);

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Property Owners
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Owners with one or more listings on VillaKosh.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/add-user")}
            className="inline-flex items-center justify-center px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Owner
          </button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email or phone"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading owners...
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            {searchQuery ? `No owners match "${searchQuery}"` : "No owners yet."}
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
            {filtered.map((o) => (
              <div
                key={o.email}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start gap-4">
                  <img
                    src={o.photoURL || "/placeholder.svg"}
                    alt={o.name}
                    className="w-14 h-14 rounded-full object-cover bg-gray-100 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {o.name}
                    </h3>
                    <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                      {o.email && (
                        <p className="flex items-center gap-1.5 truncate">
                          <Mail className="h-3 w-3 shrink-0" /> {o.email}
                        </p>
                      )}
                      {o.whatsappNumber && (
                        <p className="flex items-center gap-1.5 truncate">
                          <Phone className="h-3 w-3 shrink-0" />{" "}
                          {o.whatsappNumber}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                    {o.ownedVillas.length}{" "}
                    {o.ownedVillas.length === 1 ? "villa" : "villas"}
                  </span>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap gap-1.5">
                    {o.ownedVillas.map((v) => (
                      <Link
                        key={v.id}
                        to={`/villa/${v.id}`}
                        className="text-xs px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-100"
                      >
                        {v.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
